import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Collapse,
  Badge,
  Avatar,
  Divider,
  Paper,
  Box,
  Stack,
  styled,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  ExpandMore,
  ChevronRight,
  LightMode,
  DarkMode,
  Dashboard as DashboardIcon,
  People,
  ShoppingBasket,
  ListAlt,
  Settings as SettingsIcon,
  BarChart,
} from '@mui/icons-material';
import { 
  AccountCircle, 
  Settings, 
  WorkspacePremium, 
  Logout 
} from '@mui/icons-material';
import { alpha, Typography, Menu, ListItemButton } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { grey, deepOrange, indigo } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 240;
const collapsedWidth = 72;

const ActiveIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 4,
  height: 24,
  background: theme.palette.primary.main,
  borderRadius: 2
}));

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary: {
      main: mode === 'light' ? '#4e73df' : indigo[200],
    },
    secondary: {
      main: mode === 'light' ? deepOrange[600] : deepOrange[300],
    },
    background: {
      default: mode === 'light' ? '#f8fafc' : '#0f172a',
      paper: mode === 'light' ? '#ffffff' : '#1e293b',
    },
    text: {
      primary: mode === 'light' ? grey[900] : grey[100],
      secondary: mode === 'light' ? grey[600] : grey[400],
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.mode === 'light' 
            ? '#fff' 
            : 'linear-gradient(195deg, rgba(30,41,59,0.98), rgba(15,23,42,0.98))',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
  },
});

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const AdminDashboard = ({children}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });
  const [open, setOpen] = useState(true);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [activeMenu, setActiveMenu] = useState('Dashboard');
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setMode(mediaQuery.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('themeMode', newMode);
          return newMode;
        });
      },
    }),
    []
  );

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        setMetrics([
          { title: 'Total Users', value: '24,530', positive: true },
          { title: 'Revenue', value: '$45,200', positive: true },
          { title: 'Orders', value: '1,230',  positive: false },
          { title: 'Active Sessions', value: '892',  positive: true },
          { title: 'Conversion Rate', value: '3.2%',  positive: true },
          { title: 'Avg. Order Value', value: '$86', positive: false },
          { title: 'Pending Orders', value: '56', positive: true },
          { title: 'Support Tickets', value: '23',  positive: false },
          { title: 'Products', value: '1,450',  positive: true },
          { title: 'Monthly Visits', value: '89.4K', positive: true },
        ]);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const [user, setUser] = useState(null);
  useEffect(() => {
    const sessionUser = JSON.parse(sessionStorage.getItem("admin_user"));
    setUser(sessionUser);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("admin_user");
    setUser(null);
    navigate("/admin-login");
  };

  const menuItems = [
    { text: 'Dashboard', path: "/admin-dashboard", icon: <DashboardIcon /> },
    { text: 'Users', path: "/user-registration", icon: <People /> },
    { text: 'Transactions', path: "/product-approval", icon: <ShoppingBasket /> },
    { text: 'Geneology', path: "/admin-geneology", icon: <ListAlt /> },
    { text: 'Earnings', path: "/admin-earnings", icon: <BarChart /> },
    { 
      text: 'Product', 
      icon: <SettingsIcon />, 
      subItems: [
        { text: 'Category', path: '/product-category' }, 
        { text: 'Product', path: '/product/list' }
      ] 
    },
  ];

  const toggleSubMenu = (menu) => {
    setOpenSubMenu(openSubMenu === menu ? null : menu);
  };

  const handleMenuClick = (text, item) => {
    setActiveMenu(text);
    if (item.action) {
      item.action(); // Execute the action if it exists (like logout)
      return;
    }
    if (!item.subItems) {
      setOpenSubMenu(null);
    }
    if (item.path) {
      navigate(item.path);
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const customTheme = createTheme(getDesignTokens(mode));

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ display: 'flex' }}>
        <StyledAppBar position="fixed" color="default" sx={{
          backdropFilter: 'blur(8px)',
          backgroundColor: theme => alpha(theme.palette.background.default, 0.8),
          borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: 'none'
        }}>
          <Toolbar sx={{ gap: 2, px: { xs: 2, md: 3 }, minHeight: '64px !important' }}>
            <IconButton
              edge="start"
              onClick={() => setOpen(!open)}
              sx={{ 
                color: 'text.primary',
                '&:hover': { 
                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.1)
                }
              }}
            >
              <MenuIcon sx={{ fontSize: 28 }} />
            </IconButton>

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Tooltip title="Toggle Theme">
                <IconButton 
                  onClick={colorMode.toggleColorMode}
                  sx={{
                    p: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      transform: 'rotate(180deg)',
                      backgroundColor: theme => alpha(theme.palette.primary.main, 0.1)
                    }
                  }}
                >
                  {mode === 'dark' ? (
                    <LightMode sx={{ 
                      color: 'warning.main',
                      fontSize: 24
                    }} />
                  ) : (
                    <DarkMode sx={{ 
                      color: 'text.secondary',
                      fontSize: 24
                    }} />
                  )}
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ 
                height: 32, 
                borderColor: theme => alpha(theme.palette.divider, 0.2)
              }} />

              <Tooltip title="Notifications">
                <IconButton sx={{
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.1)
                  }
                }}>
                  <Badge 
                    badgeContent={4} 
                    color="error"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: 10,
                        height: 18,
                        minWidth: 18,
                        px: 0.5,
                        transform: 'scale(0.9) translate(50%, -50%)'
                      }
                    }}
                  >
                    <NotificationsIcon sx={{ 
                      color: 'text.secondary',
                      fontSize: 24
                    }} />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Box 
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
                sx={{ position: 'relative' }}
              >
                <Avatar 
                  src="/profile.jpg"
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    border: theme => `2px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: theme => theme.palette.primary.main,
                      transform: 'scale(1.05)'
                    }
                  }}
                />

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  disableAutoFocusItem
                  MenuListProps={{ 
                    onMouseLeave: () => setAnchorEl(null),
                    sx: { py: 0 }
                  }}
                  PaperProps={{
                    elevation: 4,
                    sx: {
                      mt: 1,
                      minWidth: 180,
                      borderRadius: 2,
                      overflow: 'visible',
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 12,
                        height: 12,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0
                      }
                    }
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <List sx={{ py: 0 }}>
                    <ListItemButton sx={{ py: 1, gap: 2 }}>
                      <AccountCircle sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body2">My Profile</Typography>
                    </ListItemButton>
                    
                    <ListItemButton sx={{ py: 1, gap: 2 }}>
                      <Settings sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body2">Account Settings</Typography>
                    </ListItemButton>
                    
                    <ListItemButton sx={{ py: 1, gap: 2 }}>
                      <WorkspacePremium sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="body2">Subscription</Typography>
                    </ListItemButton>
                  </List>

                  <Divider sx={{ my: 1 }} />

                  <List sx={{ py: 1 }}>
                    <ListItemButton 
                      sx={{ 
                        py: 1, 
                        gap: 2,
                        color: 'error.main',
                        '&:hover': { backgroundColor: 'error.light' }
                      }}
                      onClick={handleLogout}
                    >
                      <Logout sx={{ fontSize: 20 }} />
                      <Typography variant="body2">Log Out</Typography>
                    </ListItemButton>
                  </List>
                </Menu>
              </Box>
            </Stack>
          </Toolbar>
        </StyledAppBar>

        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: open ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '& .MuiDrawer-paper': {
              zIndex: theme.zIndex.drawer + 1,
              width: open ? drawerWidth : collapsedWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: open 
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
              overflowX: 'hidden',
              boxSizing: 'border-box',
              background: '#4e73df',
              backgroundImage: 'linear-gradient(180deg, #4e73df 10%, #224abe 100%)',
              backgroundSize: 'cover',
            },
          }}
        >
          <Toolbar />
          <List>
            {menuItems.map((item) => (
              <React.Fragment key={item.text}>
                <Tooltip title={item.text} placement="right" disableHoverListener={open}>
                  <ListItem 
                    button 
                    onClick={() => handleMenuClick(item.text, item)}
                    sx={{
                      py: 1,
                      cursor: "pointer",
                      position: 'relative',
                      bgcolor: activeMenu === item.text ? 'rgba(255,255,255,0.15)' : 'transparent',
                      justifyContent: open ? 'initial' : 'center',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    {activeMenu === item.text && <ActiveIndicator />}
                    <ListItemIcon sx={{ 
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      color: activeMenu === item.text ? '#fff' : 'rgba(255,255,255,0.8)'
                    }}>
                      {React.cloneElement(item.icon, { sx: { fontSize: '1.2rem' } })}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      sx={{ opacity: open ? 1 : 0 }}
                      primaryTypographyProps={{ 
                        color: activeMenu === item.text ? '#fff' : 'rgba(255,255,255,0.8)',
                        fontWeight: activeMenu === item.text ? 600 : 400,
                        fontSize: '0.95rem'
                      }} 
                    />
                    {item.subItems && open && (
                      openSubMenu === item.text ? 
                        <ExpandMore sx={{ color: 'rgba(255,255,255,0.8)' }} /> : 
                        <ChevronRight sx={{ color: 'rgba(255,255,255,0.8)' }} />
                    )}
                  </ListItem>
                </Tooltip>
                
                {item.subItems && open && (
                  <Collapse in={openSubMenu === item.text} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subItems.map((subItem) => (
                        <ListItem 
                          button 
                          key={subItem.text} 
                          sx={{ 
                            pl: 6,
                            cursor: "pointer",
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.05)'
                            }
                          }}
                          selected={activeMenu === subItem.text}
                          onClick={() => {
                            setActiveMenu(subItem.text);
                            navigate(subItem.path);
                          }}
                        >
                          <ListItemText 
                            primary={subItem.text} 
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              color: activeMenu === subItem.text ? '#fff' : 'rgba(255,255,255,0.7)',
                              fontWeight: activeMenu === subItem.text ? 500 : 400
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
            {/* Logout Button */}
            <Tooltip title="Logout" placement="right" disableHoverListener={open}>
              <ListItem 
                button 
                onClick={handleLogout}
                sx={{
                  py: 1,
                  cursor: "pointer",
                  position: 'relative',
                  justifyContent: open ? 'initial' : 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  color: 'rgba(255,255,255,0.8)'
                }}>
                  <Logout sx={{ fontSize: '1.2rem' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Logout" 
                  sx={{ opacity: open ? 1 : 0 }}
                  primaryTypographyProps={{ 
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '0.95rem'
                  }} 
                />
              </ListItem>
            </Tooltip>
          </List>
        </Drawer>

        <Box 
          component="main"
          sx={{ 
            flexGrow: 1,
            p: 1,
            background: "#f7f7f7",
            width: `calc(100% - ${open ? drawerWidth : collapsedWidth}px)`,
            marginLeft: `${open ? drawerWidth : collapsedWidth}px)`,
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar />
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default AdminDashboard;